import { useInternationalization } from '@progress/kendo-react-intl';
import { sumBy } from 'lodash';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import useVarianceDetails from 'src/hooks/useVarianceDetails';
import { round } from 'src/utils/number';

const validateNumber = (value: number) => {
  if (value > 0) {
    return 'text-[#D9534F]';
  } else if (value < 0) {
    return 'text-[#94C947]';
  }
  return '';
};

const VarianceDetailsInfo = () => {
  const { t } = useTranslation();
  const intl = useInternationalization();
  const { variances } = useVarianceDetails();

  const { subTotalVariance, qtyVariance, periodVarianceCount, glCodeVarianceCount } =
    useMemo(() => {
      const poQtyTotal = sumBy(variances.lineItemVariances, 'poQty');
      const docQtyTotal = sumBy(variances.lineItemVariances, 'invoicedQuantity');
      const subTotalVariance = round((variances?.invoiceTotal || 0) - (variances?.poTotal || 0), 2);
      const qtyVariance = round(docQtyTotal - poQtyTotal, 2);
      const periodVarianceCount =
        variances?.acctPeriodVariances?.filter(
          (lineItem) => lineItem.hasQtyVariance || lineItem.hasSubTotalVariance
        ).length || 0;
      const glCodeVarianceCount =
        variances?.lineItemVariances?.filter(
          (lineItem) => lineItem.hasGlCodeVariance || lineItem.poQty === 0
        ).length || 0;

      return {
        subTotalVariance,
        qtyVariance,
        periodVarianceCount,
        glCodeVarianceCount,
      };
    }, [
      variances?.acctPeriodVariances,
      variances?.invoiceTotal,
      variances?.lineItemVariances,
      variances?.poTotal,
    ]);

  return (
    <section className="mb-2">
      <div className="bg-[#F6F6F6] h-11 text-xs flex items-center font-medium gap-4 px-4 ">
        <>
          <span>
            {t('Total PO $ Variance')}:
            <span className={`${validateNumber(subTotalVariance)} font-bold ml-1`}>
              {intl.formatNumber(subTotalVariance, 'c2')}
            </span>
          </span>
          <span className="text-[#AAAAAA]"> | </span>
          <span>
            {t(`Total PO Quantity Variance`)}:
            <span className={`${validateNumber(qtyVariance)} font-bold ml-1`}>
              {intl.formatNumber(qtyVariance, 'c2').replace(/[$,]/g, '')}
            </span>
          </span>
          <span className="text-[#AAAAAA]"> | </span>
          <span>
            {t(`Count of Period Variances`)}:
            <span className={`font-bold ml-1`}>{periodVarianceCount}</span>
          </span>
          <span className="text-[#AAAAAA]"> | </span>
          <span>
            {t(`Count of GL Code Variances`)}:
            <span className={`font-bold ml-1`}>{glCodeVarianceCount}</span>
          </span>
        </>
      </div>
    </section>
  );
};

export default VarianceDetailsInfo;
